import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";
import type { StatusTone } from "../../design/tokens";

export type { StatusTone };

// Every tone resolves to design tokens, so status colour follows the theme,
// increased contrast, and forced colours without per-screen overrides.
const toneStyles = {
  neutral:
    "bg-tone-neutral-surface text-tone-neutral-foreground border-tone-neutral-border",
  info: "bg-tone-info-surface text-tone-info-foreground border-tone-info-border",
  success:
    "bg-tone-success-surface text-tone-success-foreground border-tone-success-border",
  warning:
    "bg-tone-warning-surface text-tone-warning-foreground border-tone-warning-border",
  danger:
    "bg-tone-danger-surface text-tone-danger-foreground border-tone-danger-border",
} satisfies Record<StatusTone, string>;

const markerStyles = {
  neutral: "bg-tone-neutral-solid",
  info: "bg-tone-info-solid",
  success: "bg-tone-success-solid",
  warning: "bg-tone-warning-solid",
  danger: "bg-tone-danger-solid",
} satisfies Record<StatusTone, string>;

const statusBadgeVariants = cva(
  "inline-flex max-w-full items-center gap-1.5 rounded-md border font-medium",
  {
    variants: {
      tone: toneStyles,
      size: {
        sm: "px-1.5 py-0.5 text-2xs",
        md: "px-2 py-0.5 text-xs",
      },
    },
    defaultVariants: { tone: "neutral", size: "md" },
  },
);

export type StatusBadgeProps = React.HTMLAttributes<HTMLSpanElement> &
  VariantProps<typeof statusBadgeVariants> & {
    /** Hides the coloured marker when the label alone is enough. */
    marker?: boolean;
  };

/**
 * A status label. The written label always carries the meaning, so the tone is
 * decoration and the badge stays readable when colour is unavailable
 * (WCAG 2.2 1.4.1 Use of Colour).
 */
export const StatusBadge = React.forwardRef<HTMLSpanElement, StatusBadgeProps>(
  ({ className, tone, size, marker = true, children, ...props }, ref) => (
    <span
      ref={ref}
      className={cn(statusBadgeVariants({ tone, size }), className)}
      {...props}
    >
      {marker && (
        <span
          aria-hidden="true"
          className={cn(
            "h-1.5 w-1.5 shrink-0 rounded-full",
            markerStyles[tone ?? "neutral"],
          )}
        />
      )}
      <span className="truncate">{children}</span>
    </span>
  ),
);
StatusBadge.displayName = "StatusBadge";

export type StatusDotProps = React.HTMLAttributes<HTMLSpanElement> & {
  tone?: StatusTone;
  /** Read out to assistive technology in place of the missing visible text. */
  label: string;
};

/**
 * The compact form for dense table cells. The label is exposed to assistive
 * technology because the dot on its own conveys nothing without colour.
 */
export const StatusDot = React.forwardRef<HTMLSpanElement, StatusDotProps>(
  ({ className, tone = "neutral", label, ...props }, ref) => (
    <span
      ref={ref}
      className={cn("inline-flex items-center gap-2", className)}
      {...props}
    >
      <span
        aria-hidden="true"
        className={cn("h-2 w-2 shrink-0 rounded-full", markerStyles[tone])}
      />
      <span className="sr-only">{label}</span>
    </span>
  ),
);
StatusDot.displayName = "StatusDot";

export { statusBadgeVariants };
